<template>
    <div class="px-3">
        <!-- Title -->
        <div class="row">
            <div class="col-12">
                <div class="d-flex mb-3">
                    <div class="mr-auto d-flex align-items-center">
                        <h5 class="text-dark mb-0">RCS Business Messaging</h5>
                        <i :class="rcsTooltip?.iconClass" class="ml-2 material-icons">
                          {{ rcsTooltip?.iconName }}
                        </i>
                        <p class="ml-2 mb-0 font-120 d-flex align-items-center font-weight-bold">
                          <span :class="rcsTooltip?.iconClass">{{
                            rcsTooltip?.notice
                          }}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <el-steps
            class="mb-3"
            :active="currentStep - 1"
            align-center
            finish-status="success">
            <el-step title="Information" />
            <el-step title="Brand Details" />
            <el-step title="Review & Submit" />
        </el-steps>

        <!-- Step 1: RCS Information -->
        <div v-if="currentStep === 1" class="mt-2">
            <div class="row mb-4 justify-content-center">
                <div class="col-md-5 mb-4 mb-lg-0 d-flex flex-column">
                    <div class="rcs-hero-card rounded-lg p-4 d-flex flex-column mb-4">
                        <h2 class="rcs-hero-title">
                          Stop sending messages<br />
                          from "unknown numbers."
                        </h2>
                        <p class="rcs-hero-description my-3">
                          80% of customers ignore messages from unrecognized senders. RCS
                          puts your logo, verified business name, and a trust checkmark on
                          every message so customers recognize you, trust you, and actually
                          respond.
                        </p>
                        <div class="d-flex mt-auto">
                            <div class="rcs-stat-card text-center mr-2 flex-1">
                              <div class="rcs-stat-number">88%</div>
                              <div class="rcs-stat-label">
                                more likely to engage with a verified sender
                              </div>
                            </div>
                            <div class="rcs-stat-card text-center mr-2 flex-1">
                              <div class="rcs-stat-number">45%</div>
                              <div class="rcs-stat-label">
                                conversion rate with rich media and tap-to-act buttons
                              </div>
                            </div>
                            <div class="rcs-stat-card text-center mr-2 flex-1s">
                              <div class="rcs-stat-number">58%</div>
                              <div class="rcs-stat-label">
                                reply rate — customers respond, not just read
                              </div>
                            </div>
                        </div>
                        <div class="mt-3">
                            <a
                              href="https://aloware.com/products/rich-communication-services"
                              target="_blank"
                              class="rcs-hero-link"
                            >
                              Learn more about RCS Messaging
                              <i class="fa fa-arrow-right ml-1"></i>
                            </a>
                        </div>
                    </div>
                      <div class="d-flex justify-content-end align-items-center">
                          <el-button
                            v-if="!hasRcsOrder"
                            type="primary"
                            size="medium"
                            @click="goToStep(2)"
                          >
                            Sign up
                            <i class="fa fa-arrow-right ml-1"></i>
                          </el-button>
                      </div>
                  </div>
                <div class="col-md-5 d-flex justify-content-center align-items-center">
                    <div class="w-50 ratio ratio-9x16 d-inline-block px-3 py-4 phone-card">
                        <video class="w-100" autoplay loop muted>
                          <source src="/videos/rcs-demo.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 2: Company Details with live preview -->
        <div v-if="currentStep === 2" class="row mt-2 border border p-3">
            <div class="col-lg-6 mb-4 mb-lg-0">
                <el-card class="company-details-form-card border-0" shadow="never">
                    <div slot="header">
                        <strong>Brand Information</strong>
                    </div>
                    <el-form
                        ref="companyForm"
                        :model="animationConfig"
                        :rules="formRules"
                        label-position="top"
                        class="form-aloware company-form-static"
                    >
                        <el-form-item label="Brand Name" prop="brandName" class="border-0">
                            <el-input
                                v-model="animationConfig.brandName"
                                placeholder="Enter brand name"
                                maxlength="100"
                                show-word-limit
                            >
                          </el-input>
                        </el-form-item>
                        <el-form-item label="Brand Description" prop="description">
                            <el-input
                                v-model="animationConfig.description"
                                type="textarea"
                                :rows="2"
                                placeholder="Enter description"
                                maxlength="100"
                                show-word-limit
                            >
                            </el-input>
                        </el-form-item>
                        <el-form-item
                            label="Logo Image"
                            class="border-0 mb-0"
                            :error="formErrors.logo"
                        >
                            <div class="d-flex justify-content-start">
                                <el-upload
                                    class="mt-2"
                                    action="#"
                                    :auto-upload="false"
                                    :show-file-list="false"
                                    :on-change="handleIconUpload"
                                    accept="image/jpeg,image/jpg,image/png"
                                >
                                    <el-button size="small" type="primary">
                                      <i class="fa fa-upload"></i> Upload Logo
                                    </el-button>
                                </el-upload>
                            </div>
                            <div class="text-muted small mt-1">
                                Recommended: 224×224 pixels, Max 50KB, JPEG/JPG/PNG only
                            </div>
                        </el-form-item>
                        <el-form-item
                            label="Banner Image"
                            class="border-0 mb-0"
                            :error="formErrors.banner"
                        >
                            <div class="d-flex justify-content-start">
                                <el-upload
                                    action="#"
                                    :auto-upload="false"
                                    :show-file-list="false"
                                    :on-change="handleBannerUpload"
                                    accept="image/jpeg,image/jpg,image/png"
                                >
                                  <el-button size="small" type="primary">
                                      <i class="fa fa-upload"></i> Upload Banner
                                  </el-button>
                                </el-upload>
                            </div>
                            <div class="text-muted small mt-1">
                                Recommended: 1440×448 pixels, Max 200KB, JPEG/JPG/PNG only
                            </div>
                        </el-form-item>
                        <el-form-item
                            label="Accent Color"
                            class="border-0 mb-0"
                            :error="accentColorWarning"
                        >
                            <el-color-picker
                                v-model="animationConfig.accentColor"
                                @change="validateAccentColor"
                            >
                            </el-color-picker>
                            <span class="ml-2">{{ animationConfig.accentColor }}</span>
                            <div class="text-muted small mt-1">
                                Must have a contrast ratio of at least 4.5:1 relative to white
                            </div>
                        </el-form-item>
                    </el-form>

                    <div class="mt-1 pt-3 border-top">
                        <strong class="d-block mb-3">Brand Contact Information</strong>
                        <el-form>
                            <div
                                v-for="(block, index) in contactDetails"
                                :key="'contact-' + index"
                                class="contact-detail-block px-3 pb-4 mb-3 rounded border"
                            >
                                <div class="font-weight-bold pt-3 mb-2">
                                    <i
                                      :class="contactTypeIcon(block.contactType)"
                                      class="mr-1"
                                    ></i>
                                    {{ contactTypeLabel(block.contactType) }}
                                </div>
                                <el-form-item
                                    :label="primaryFieldLabel(block.contactType)"
                                    :error="block.error"
                                >
                                    <el-input
                                      v-model="block.primary"
                                      :placeholder="primaryPlaceholder(block.contactType)"
                                      @input="validatePrimary(block)"
                                    >
                                    </el-input>
                                </el-form-item>
                                <div
                                    v-if="block.contactType === 'phone'"
                                    class="text-muted small"
                                    :class="[block.error ? 'mt-4' : '']"
                                >
                                    Must be in E.164 format (e.g., +1234567890)
                                </div>
                                <el-form-item
                                    :label="labelForFieldLabel(block.contactType)"
                                    :error="block.errorLabel"
                                >
                                    <el-input v-model="block.label" @input="validateLabel(block)">
                                    </el-input>
                                </el-form-item>
                            </div>
                        </el-form>
                    </div>

                    <el-form
                        ref="companyFormLinks"
                        label-position="top"
                        class="mt-4 pt-3 border-top"
                        :model="animationConfig"
                        :rules="formLinkRules"
                    >
                        <el-form-item
                            label="Link to Privacy Policy"
                            prop="privacyPolicyUrl"
                        >
                            <el-input
                              v-model="animationConfig.privacyPolicyUrl"
                              placeholder="Add website link to privacy policy"
                            >
                            </el-input>
                        </el-form-item>
                        <el-form-item
                            label="Link to Terms of Service"
                            prop="termsOfServiceUrl"
                        >
                            <el-input
                              v-model="animationConfig.termsOfServiceUrl"
                              placeholder="Add website link to terms of service"
                            >
                            </el-input>
                        </el-form-item>
                    </el-form>

                    <div class="mt-4 justify-content-end d-flex">
                        <el-button @click="goToStep(1)"> Back</el-button>
                        <el-button type="primary" @click="goToStep(3)"> Next</el-button>
                    </div>
                </el-card>
            </div>
            <div class="col-lg-6 justify-content-center align-items-start mt-3">
                <rcs-live-preview
                    :config="animationConfig"
                    :contact-details="contactDetails"
                    :contact-type-options="contactTypeOptions"
                >
                </rcs-live-preview>
            </div>
        </div>

        <!-- Step 3: RCS Pricing & Submission -->
        <div v-if="currentStep === 3" class="mt-2 border d-flex row p-3">
            <div class="col-lg-6 d-flex flex-column gap-3 justify-content-center">
                <div>
                    <el-card class="mt-3" shadow="never">
                        <div slot="header">
                          <strong class="ml-2">RCS Pricing</strong>
                        </div>
                        <div v-if="current_company && current_company.rate">
                            <p class="mb-2">For RCS registration in the US</p>

                            <table class="table table-sm table-bordered mb-0">
                                <thead class="bg-light">
                                    <tr>
                                        <th>Fee Type</th>
                                        <th class="text-right">Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>RCS Brand Registration Fee</td>
                                        <td class="text-right">
                                          <b>$1,000 / year</b>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <p class="text-muted small mt-2 mb-0">
                                <i class="fa fa-info-circle"></i>
                                This fee is charged upfront upon submission to the carrier and fully refundable if it is not approved.
                            </p>
                        </div>
                    </el-card>
                </div>
                <div>
                    <el-card class="mt-3" shadow="never">
                        <div slot="header">
                            <strong class="ml-2">RCS Rate Pricing</strong>
                        </div>
                        <div v-if="current_company && current_company.rate">
                            <p class="mb-2">
                                <b>Your Rate Tier:</b>
                                <el-tag class="ml-2">
                                    {{ rateTierName }}
                                </el-tag>
                            </p>

                            <table class="table table-sm table-bordered mb-0">
                                <thead class="bg-light">
                                    <tr>
                                        <th>Product Type</th>
                                        <th class="text-right">Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>RCS Message</td>
                                        <td class="text-right">
                                            ${{ formatPrice(current_company.rate.rcs) }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>RCS Media Message</td>
                                        <td class="text-right">
                                            ${{ formatPrice(current_company.rate.rcs_media) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <p class="text-muted small mt-2 mb-0">
                                <i class="fa fa-info-circle"></i>
                                Rates are per message and will be deducted from your credit balance.
                            </p>
                        </div>
                    </el-card>
                </div>
                <div class="mt-4 p-3 bg-light rounded border">
                    <h6 class="font-weight-bold mb-2">What happens next?</h6>
                    <div class="d-flex align-items-start">
                        <ul class="mb-0" style="padding-left: 1.5rem">
                            <li class="mb-2">
                              <strong>Registration Charge:</strong> This will be processed upon submission to the carrier.
                            </li>
                            <li class="mb-2">
                              <strong>Carrier Approval:</strong> Your request is pending
                              approval from major carriers.
                            </li>
                            <li>
                              <strong>Setup Timeline:</strong> Please allow up to 6 weeks to
                              complete the setup process. If you're launching in multiple
                              regions, additional time may be required.
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="mt-3 justify-content-end d-flex">
                    <el-button @click="goToStep(2)"> Back</el-button>
                    <el-button
                        type="primary"
                        :loading="loading"
                        :disabled="has_order"
                        @click="showConfirmationModal = true"
                    >
                        Review and Submit
                    </el-button>
                </div>
            </div>
            <div class="col-lg-6 justify-content-center align-items-start mt-3">
                <rcs-live-preview
                    :config="animationConfig"
                    :contact-details="contactDetails"
                    :contact-type-options="contactTypeOptions"
                >
                </rcs-live-preview>
            </div>
        </div>

        <div class="row" v-loading="loading_data" v-if="loading_data">
            <div class="col-12" style="min-height: 200px"></div>
        </div>

        <!-- Confirmation Modal -->
        <el-dialog
            title="Confirm RCS Registration"
            :visible.sync="showConfirmationModal"
            width="480px"
            :close-on-click-modal="false"
        >
            <p>
                Are you sure you want to submit your RCS brand registration request?
            </p>
            <p class="text-muted mb-0">
                A $1,000/year brand registration fee will be charged to your account.
                This fee is fully refundable if your brand is not approved.
            </p>
            <span slot="footer" class="dialog-footer">
                <el-button @click="showConfirmationModal = false">Cancel</el-button>
                <el-button type="primary" :loading="loading" @click="confirmAndSubmit">
                  Confirm & Submit
                </el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import * as RcsOrderStatus from '../../constants/rcs-order-status'
import RcsLivePreview from './rcs-live-preview.vue'

export default {
    name: 'RcsTab',

    components: {
        RcsLivePreview
    },

    data() {
        return {
            currentStep: 1,
            loading: false,
            loading_data: true,
            has_order: false,
            rcs_order: {
                status: null
            },
            icons: [
                {
                  id: 0,
                  class: 'text-grey',
                  name: 'remove_circle',
                  status: 'Not started',
                  notice: '[Not Started]'
                },
                {
                  id: RcsOrderStatus.RCS_STATUS_SUBMITTED,
                  class: 'text-blue',
                  name: 'access_time_filled',
                  status: 'Submitted',
                  notice: '[Submitted]'
                },
                {
                  id: RcsOrderStatus.RCS_STATUS_IN_REVIEW,
                  class: 'text-blue',
                  name: 'access_time_filled',
                  status: 'In Review',
                  notice: '[In Review]'
                },
                {
                  id: RcsOrderStatus.RCS_STATUS_REJECTED,
                  class: 'text-danger',
                  name: 'cancel',
                  status: 'Suspended',
                  notice: '[Needs Attention]'
                },
                {
                  id: RcsOrderStatus.RCS_STATUS_APPROVED,
                  class: 'text-green',
                  name: 'check_circle_outline',
                  status: 'Approved',
                  notice: '[Approved]'
                }
            ],
            animationConfig: {
                brandName: '',
                logo: '',
                logo_file: '',
                description: '',
                banner: '',
                banner_file: '',
                accentColor: '#313131',
                privacyPolicyUrl: '',
                termsOfServiceUrl: ''
            },
            contactDetails: [
                {
                  contactType: 'phone',
                  primary: '',
                  label: '',
                  error: '',
                  errorLabel: ''
                },
                {
                  contactType: 'email',
                  primary: '',
                  label: '',
                  error: '',
                  errorLabel: ''
                },
                {
                  contactType: 'website',
                  primary: '',
                  label: '',
                  error: '',
                  errorLabel: ''
                }
            ],
            contactTypeOptions: [
                { value: 'phone', label: 'Phone Number' },
                { value: 'email', label: 'Email' },
                { value: 'website', label: 'Website' }
            ],
            showConfirmationModal: false,
            accentColorWarning: '',
            formErrors: {
                logo: '',
                banner: ''
            },
            formRules: {
                brandName: [
                    {
                      required: true,
                      message: 'Brand name is required',
                      trigger: 'blur'
                    },
                    {
                      min: 1,
                      max: 100,
                      message: 'Brand name must be between 1 and 100 characters',
                      trigger: 'blur'
                    }
                ],
                description: [
                    {
                      required: true,
                      message: 'Brand description is required',
                      trigger: 'blur'
                    },
                    {
                      min: 1,
                      max: 100,
                      message: 'Description must be between 1 and 100 characters',
                      trigger: 'blur'
                    }
                ]
            },
            formLinkRules: {
                privacyPolicyUrl: [
                    {
                      required: true,
                      message: 'Privacy policy url is required',
                      trigger: 'blur'
                    }
                ],
                termsOfServiceUrl: [
                    {
                      required: true,
                      message: 'Terms of service url is required',
                      trigger: 'blur'
                    }
                ]
            }
        }
    },

    computed: {
        ...mapState('cache', ['current_company']),

        rcsTooltip() {
            // If there's no status, show "Not started" icon
            if (!this.rcs_order.status) {
                const icon = this.icons.find(({ id }) => id === 0)

                return {
                    content: icon.status,
                    iconClass: icon.class,
                    iconName: icon.name,
                    notice: icon.notice
                }
            }

            // Find the matching icon
            const icon = this.icons.find(({ id }) => id === this.rcs_order.status)

            // Fallback to "Not started" if no match found
            if (!icon) {
                const fallbackIcon = this.icons.find(({ id }) => id === 0)
                return {
                  content: fallbackIcon.status,
                  iconClass: fallbackIcon.class,
                  iconName: fallbackIcon.name,
                  notice: fallbackIcon.notice
                }
            }

            return {
                content: icon.status,
                iconClass: icon.class,
                iconName: icon.name,
                notice: icon.notice
            }
        },

        rateTierName() {
            if (!this.current_company || !this.current_company.rate) {
                return 'Standard'
            }

            return this.current_company.rate.name
        },

        hasRcsOrder() {
            return this.has_order || this.current_company.has_rcs_order
        }
    },

    created() {
        this.fetchRcsOrder()
        this.initializeAnimationConfig()
        this.validateAccentColor(this.animationConfig.accentColor)
    },

    methods: {
        ...mapActions('cache', ['setCurrentCompany']),

        initializeAnimationConfig() {
            if (this.current_company) {
                this.animationConfig.brandName = this.current_company.name || ''
            }
        },

        handleIconUpload(file) {
            this.formErrors.logo = ''

            const maxSize = 50 * 1024
            const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png']

            if (!allowedFormats.includes(file.raw.type)) {
                this.formErrors.logo = 'Logo image must be JPEG, JPG, or PNG format'
                return
            }

            if (file.raw.size > maxSize) {
                this.formErrors.logo = 'Logo image file size must not exceed 50KB'
                return
            }

            const img = new Image()
            const reader = new FileReader()

            reader.onload = (e) => {
                img.src = e.target.result

                img.onload = () => {
                    const width = img.width
                    const height = img.height

                    if (Math.abs(width - 224) > 50 || Math.abs(height - 224) > 50) {
                        this.$message.warning(
                          `Logo dimensions are ${width}×${height}. Recommended: 224×224 pixels`
                        )
                    }

                    this.animationConfig.logo = e.target.result
                }
            }

            this.animationConfig.logo_file = file.raw
            reader.readAsDataURL(file.raw)
        },

        handleBannerUpload(file) {
            this.formErrors.banner = ''

            const maxSize = 200 * 1024
            const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png']

            if (!allowedFormats.includes(file.raw.type)) {
                this.formErrors.banner = 'Banner image must be JPEG, JPG, or PNG format'
                return
            }

            if (file.raw.size > maxSize) {
                this.formErrors.banner = 'Banner image file size must not exceed 200KB'
                return
            }

            const img = new Image()
            const reader = new FileReader()

            reader.onload = (e) => {
                img.src = e.target.result

                img.onload = () => {
                    const width = img.width
                    const height = img.height

                    if (Math.abs(width - 1440) > 100 || Math.abs(height - 448) > 50) {
                        this.$message.warning(
                          `Banner dimensions are ${width}×${height}. Recommended: 1440×448 pixels`
                        )
                    }

                    this.animationConfig.banner = e.target.result
                }
            }

            this.animationConfig.banner_file = file.raw
            reader.readAsDataURL(file.raw)
        },

        fetchRcsOrder() {
            this.loading_data = true

            this.$APIV2.RcsOrders.fetchRcsOrders()
                .then((res) => {
                    this.has_order = res.data.has_order

                    if (res.data.rcs_order) {
                        // Preserve reactivity by merging the order data
                        this.rcs_order = {
                          ...this.rcs_order,
                          ...res.data.rcs_order[0]
                        }
                    }
                })
                .catch((err) => {
                    this.$root.handleErrors(err.response)
                })
                .finally(() => {
                    this.loading_data = false
                })
        },

        async goToStep(step) {
            if (step === 3 && this.currentStep === 2) {
                let isValid = await this.validData()
                if (!isValid) {
                    return
                }
            }

            this.currentStep = step
        },

        async validData() {
            let formValid = false
            let linksValid = false

            try {
                formValid = await this.$refs.companyForm.validate()
            } catch (e) {
                formValid = false
            }

            try {
                linksValid = await this.$refs.companyFormLinks.validate()
            } catch (e) {
                linksValid = false
            }

            if (
                !this.validateBeforeSubmit() ||
                this.accentColorWarning ||
                !formValid ||
                !linksValid
            ) {
                this.$message.error('Please fill in all required fields correctly')
                return false
            }

            return true
        },

        contactTypeLabel(contactType) {
            const labels = {
                phone: 'Phone Number',
                email: 'Email',
                website: 'Website'
            }
            return labels[contactType] || contactType
        },

        contactTypeIcon(contactType) {
            const icons = {
                phone: 'fa fa-phone',
                email: 'fa fa-envelope',
                website: 'fa fa-globe'
            }
            return icons[contactType] || 'fa fa-info-circle'
        },

        primaryFieldLabel(contactType) {
            const labels = {
                phone: 'Primary phone number',
                email: 'Primary email address',
                website: 'Primary website url'
            }
            return labels[contactType] || 'Primary'
        },

        primaryPlaceholder(contactType) {
            const placeholders = {
                phone: 'e.g. +1234567890',
                email: 'e.g. contact@company.com',
                website: 'e.g. https://example.com'
            }
            return placeholders[contactType] || ''
        },

        labelForFieldLabel(contactType) {
            const labels = {
                phone: 'Label for phone number',
                email: 'Label for email address',
                website: 'Label for website'
            }
            return labels[contactType] || 'Label For'
        },

        validateAccentColor(color) {
            if (!color) return

            const contrastRatio = this.calculateContrastRatio(color, '#FFFFFF')

            if (contrastRatio < 4.5) {
                this.accentColorWarning = `Contrast ratio is ${contrastRatio.toFixed(2)}:1. Must be at least 4.5:1 for accessibility.`
            } else {
                this.accentColorWarning = ''
            }
        },

        calculateContrastRatio(color1, color2) {
            const getLuminance = (hexColor) => {
                const rgb = this.hexToRgb(hexColor)
                const rsRGB = rgb.r / 255
                const gsRGB = rgb.g / 255
                const bsRGB = rgb.b / 255

                const r =
                  rsRGB <= 0.03928
                    ? rsRGB / 12.92
                    : Math.pow((rsRGB + 0.055) / 1.055, 2.4)
                const g =
                  gsRGB <= 0.03928
                    ? gsRGB / 12.92
                    : Math.pow((gsRGB + 0.055) / 1.055, 2.4)
                const b =
                  bsRGB <= 0.03928
                    ? bsRGB / 12.92
                    : Math.pow((bsRGB + 0.055) / 1.055, 2.4)

                return 0.2126 * r + 0.7152 * g + 0.0722 * b
            }

            const lum1 = getLuminance(color1)
            const lum2 = getLuminance(color2)

            const lighter = Math.max(lum1, lum2)
            const darker = Math.min(lum1, lum2)

            return (lighter + 0.05) / (darker + 0.05)
        },

        hexToRgb(hex) {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
            return result
              ? {
                  r: parseInt(result[1], 16),
                  g: parseInt(result[2], 16),
                  b: parseInt(result[3], 16)
                }
              : { r: 0, g: 0, b: 0 }
        },

        validatePhoneNumber(phone) {
            const e164Pattern = /^\+[1-9]\d{1,14}$/
            return e164Pattern.test(phone)
        },

        validateEmail(email) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailPattern.test(email)
        },

        validateWebsiteUrl(url) {
            try {
                const urlObj = new URL(url)
                return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
            } catch (e) {
                return false
            }
        },

        validateBeforeSubmit() {
            this.formErrors = {
              logo: '',
              banner: ''
            }
            let isValid = true
            Object.keys(this.animationConfig).forEach((field) => {
                if (
                    this.animationConfig[field] &&
                    this.animationConfig[field].toString().trim() === ''
                ) {
                    this.formErrors[field] = `${field} is required`
                    isValid = false
                } else {
                    this.formErrors[field] = ''
                }
            })

            if (this.accentColorWarning) {
                isValid = false
            }

            if (
              !this.animationConfig.logo ||
              this.animationConfig.logo.trim().length === 0
            ) {
                this.formErrors.logo = 'Logo image is required'
                isValid = false
            }

            if (
                !this.animationConfig.banner ||
                this.animationConfig.banner.trim().length === 0
            ) {
                this.formErrors.banner = 'Banner image is required'
                isValid = false
            }

            const phoneContacts = this.contactDetails.filter(
                (c) => c.contactType === 'phone'
            )
            if (phoneContacts.length > 0) {
                phoneContacts.forEach((phoneContact) => {
                  isValid &= this.validateContactDetail(phoneContact)
                })
            }

            const emailContacts = this.contactDetails.filter(
                (c) => c.contactType === 'email'
            )
            if (emailContacts.length > 0) {
                emailContacts.forEach((emailContact) => {
                  isValid &= this.validateContactDetail(emailContact)
                })
            }

            const websiteContacts = this.contactDetails.filter(
                (c) => c.contactType === 'website'
            )
            if (websiteContacts.length > 0) {
                websiteContacts.forEach((websiteContact) => {
                  isValid &= this.validateContactDetail(websiteContact)
                })
            }

            return isValid
        },

        confirmAndSubmit() {
            this.showConfirmationModal = false
            this.processOrder()
        },

        processOrder() {
            this.loading = true

            const formData = new FormData()

            formData.append('company_name', this.animationConfig.brandName || '')
            formData.append('description', this.animationConfig.description || '')
            formData.append('accent_color', this.animationConfig.accentColor || '')
            formData.append(
              'privacy_policy_url',
              this.animationConfig.privacyPolicyUrl || ''
            )
            formData.append(
              'terms_of_service_url',
              this.animationConfig.termsOfServiceUrl || ''
            )
            formData.append('contact_details', JSON.stringify(this.contactDetails))

            if (this.animationConfig.logo_file) {
              formData.append('logo', this.animationConfig.logo_file)
            }

            if (this.animationConfig.banner_file) {
              formData.append('banner', this.animationConfig.banner_file)
            }

            this.$APIV2.RcsOrders.createRcsOrder(formData)
                .then((res) => {
                    this.$notify({
                        offset: 95,
                        title: 'Success',
                        message:
                          'Your RCS order has been submitted successfully. Our team will contact you soon.',
                        type: 'success',
                        showClose: true,
                        duration: 5000
                    })

                    // Update state with reactive merge
                    this.has_order = true

                    this.rcs_order = res.data.rcs_order
                    this.setCurrentCompany(res.data.company)

                    this.goToStep(1)
                })
                .catch((err) => {
                    this.$root.handleErrors(err.response)
                })
                .finally(() => {
                    this.loading = false
                })
        },

        infoLabel(contactType) {
            return this.contactTypeOptions.find(
              (contactTypeOption) => contactTypeOption.value === contactType
            )?.label
        },

        formatPrice(price) {
            return parseFloat(price).toFixed(4)
        },

        validateContactDetail(contact) {
            const validLabel = this.validateLabel(contact)
            const validPrimary = this.validatePrimary(contact)

            return validLabel && validPrimary
        },

        validatePrimary(contact) {
            let valid = true
            contact.error = ''

            if (!contact.primary || contact.primary.trim().length === 0) {
              contact.error = this.infoLabel(contact.contactType) + ' is required'
              valid = false
            } else {
                switch (contact.contactType) {
                    case 'phone':
                        valid = this.validatePhoneNumber(contact.primary)
                        contact.error = valid
                          ? ''
                          : 'Phone Number must be in E.164 format (e.g., +1234567890)'
                        break
                    case 'email':
                        valid = this.validateEmail(contact.primary)
                        contact.error = valid
                          ? ''
                          : 'Email address must be valid (e.g., user@example.com)'

                        break
                    case 'website':
                        valid = this.validateWebsiteUrl(contact.primary)
                        contact.error = valid
                          ? ''
                          : 'Website URL must be a valid URL (e.g., https://example.com)'

                        break
                }
            }

            return valid
        },

        validateLabel(contact) {
            let valid = true
            contact.errorLabel = ''

            if (!contact.label || contact.label.trim().length === 0) {
                contact.errorLabel = `Label for ${this.infoLabel(contact.contactType)} is required`
                valid = false
            }

            return valid
        }
    }
}
</script>

<style scoped>
.rcs-hero-card {
  background: linear-gradient(135deg, #1a2744 0%, #2c4a7c 50%, #3a6098 100%);
  border-radius: 16px;
  color: #fff;
}

.rcs-hero-title {
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
}

.rcs-hero-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}

.rcs-stat-card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 20px 28px;
  text-align: center;
  flex: 1;
}

.rcs-stat-number {
  font-size: 32px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.rcs-stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
}

.rcs-hero-link {
  display: inline-block;
  color: #fff;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
}
</style>
